import { api } from "./client";
import type { AdminUser, EligibilityPeriod, FoodBank, Group, UserRole } from "./types";

// --- Food banks (SUPER_ADMIN only for list/create; SUPER_ADMIN + FOOD_BANK_ADMIN for update/dashboard) ---

export async function listFoodBanks() {
  const res = await api.get<FoodBank[]>("/admin/food-banks");
  return res.data;
}

export async function createFoodBank(name: string) {
  const res = await api.post<FoodBank>("/admin/food-banks", { name });
  return res.data;
}

export async function updateFoodBank(foodBankId: string, name: string) {
  const res = await api.patch<FoodBank>(`/admin/food-banks/${foodBankId}`, { name });
  return res.data;
}

export async function foodBankDashboard(foodBankId: string) {
  const res = await api.get<{ groups: Group[] }>(`/admin/food-banks/${foodBankId}/dashboard`);
  return res.data;
}

// --- Groups ---

export async function listGroups(includeArchived?: boolean) {
  const res = await api.get<Group[]>("/admin/groups", {
    params: includeArchived ? { includeArchived: true } : {},
  });
  return res.data;
}

export async function getGroup(groupId: string) {
  const res = await api.get<Group>(`/admin/groups/${groupId}`);
  return res.data;
}

export interface GroupFormValues {
  foodBankId: string;
  shortCode: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  fax?: string;
  email?: string;
  contactFirstName?: string;
  contactLastName?: string;
  eligibilityPeriod?: EligibilityPeriod;
  tracksSpecialDistribution?: boolean;
  paperlessUsdaForm?: boolean;
  hasSignaturePad?: boolean;
  usdaRecertRequired?: boolean;
  usdaRecertDays?: number;
  requireLoginInitials?: boolean;
  showEmployed?: boolean;
  showMedicare?: boolean;
  showChip?: boolean;
  showWic?: boolean;
}

export async function createGroup(data: GroupFormValues) {
  const res = await api.post<Group>("/admin/groups", data);
  return res.data;
}

export async function updateGroup(groupId: string, data: Partial<Omit<GroupFormValues, "foodBankId">>) {
  const res = await api.patch<Group>(`/admin/groups/${groupId}`, data);
  return res.data;
}

export async function archiveGroup(groupId: string) {
  const res = await api.post<Group>(`/admin/groups/${groupId}/archive`);
  return res.data;
}

export async function unarchiveGroup(groupId: string) {
  const res = await api.post<Group>(`/admin/groups/${groupId}/unarchive`);
  return res.data;
}

// --- Users ---

export async function listUsers(groupId?: string) {
  const res = await api.get<AdminUser[]>("/admin/users", { params: groupId ? { groupId } : {} });
  return res.data;
}

export interface CreateUserInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  initials?: string;
  room?: string;
  canChangePassword?: boolean;
  role: UserRole;
  groupId?: string;
  foodBankId?: string;
}

export async function createUser(data: CreateUserInput) {
  const res = await api.post("/admin/users", data);
  return res.data;
}

// Mirrors the backend's updateUserSchema (user.controller.ts): role/groupId/
// foodBankId are optional here for the same reason they're optional there -
// most edits (name, initials, room, password reset) don't touch them, but
// the role-change feature needs them accepted when they ARE sent.
export interface UpdateUserInput {
  firstName?: string;
  lastName?: string;
  initials?: string;
  room?: string;
  password?: string;
  canChangePassword?: boolean;
  role?: UserRole;
  groupId?: string;
  foodBankId?: string;
}

export async function updateUser(userId: string, data: UpdateUserInput) {
  const res = await api.patch(`/admin/users/${userId}`, data);
  return res.data;
}

export async function deactivateUser(userId: string) {
  const res = await api.post<{ id: string; active: boolean }>(`/admin/users/${userId}/deactivate`);
  return res.data;
}